import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../ui/ToastProvider'
import Loading from '../ui/Loading'
import PublicFooter from '../ui/PublicFooter'
import { GoogleLogin } from '@react-oauth/google'
import { kakaoLogin } from '../lib/kakao'
import { naverLogin } from '../lib/naver'
import { postLoginPathFromRouterState, consumeStashedLoginReturnPath, clearStashedLoginReturnPath } from '../lib/authReturnPath'
const LOGIN_LOGO_URL = `${process.env.PUBLIC_URL}/logo512.png`

function getLoginErrorMessage(err) {
    const status = err?.response?.status
    // BE는 일부 케이스에서 ApiResponse.success(null)을 내려주기도 해서(401/423),
    // message는 data/data.message 까지 넓게 확인한다.
    const apiMsg =
        err?.response?.data?.data?.message
        || err?.response?.data?.message
        || err?.response?.data?.error?.message

    if (apiMsg) return String(apiMsg)

    // status 기반 한글 안내
    if (status === 401) {
        return '이메일 또는 비밀번호가 올바르지 않습니다.\n- 대/소문자 및 Caps Lock을 확인해주세요.\n- 비밀번호가 기억나지 않으면 “비밀번호 찾기”를 이용해주세요.'
    }
    if (status === 403) {
        return '해당 계정은 현재 로그인할 수 없습니다.\n- 승인 대기 중이거나 비활성/정지된 계정일 수 있습니다.\n- 관리자(운영자)에게 계정 상태를 확인 요청해주세요.'
    }
    if (status === 423) {
        // BE 정책: 실패 5회 이상 시 15분 잠금
        return '비밀번호를 여러 번(5회 이상) 잘못 입력하여 계정이 잠겨 있습니다.\n- 15분 후 다시 시도해주세요.\n- 비밀번호가 기억나지 않으면 “비밀번호 찾기”를 이용해주세요.\n- 잠금이 반복되면 관리자(운영자)에게 문의해주세요.'
    }

    if (err?.code === 'ERR_NETWORK') {
        return '네트워크 오류로 로그인할 수 없습니다. 인터넷 연결 또는 서버 상태를 확인해주세요.'
    }

    if (status) return `로그인에 실패했습니다. (오류 코드: ${status})`
    return err?.message || '로그인에 실패했습니다'
}

export default function LoginPage() {
    const { login, loginWithGoogle, loginWithKakao, loginWithNaverCode } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const { show } = useToast()
    const resolvePostLoginTarget = React.useCallback(() => {
        const fromState = postLoginPathFromRouterState(location.state?.from)
        if (fromState) {
            clearStashedLoginReturnPath()
            return fromState
        }
        return consumeStashedLoginReturnPath() || '/'
    }, [location.state])
    const emailHint = location.state && location.state.emailHint ? String(location.state.emailHint) : null

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const googleEnabled = String(process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim().length > 0
    const kakaoEnabled = String(process.env.REACT_APP_KAKAO_JS_KEY || '').trim().length > 0
    const naverEnabled = String(process.env.REACT_APP_NAVER_CLIENT_ID || '').trim().length > 0
    const socialConfigured = googleEnabled || kakaoEnabled || naverEnabled

    // 세션 플래시(로그인 만료 등) 표시
    React.useEffect(() => {
        try {
            const raw = sessionStorage.getItem('flash.login')
            if (!raw) return
            sessionStorage.removeItem('flash.login')
            const v = JSON.parse(raw)
            const msg = v?.message ? String(v.message) : null
            const variant = v?.variant ? String(v.variant) : 'info'
            if (msg) show(msg, variant, 2500)
        } catch { }
    }, [show])

    const onSubmit = async (e) => {
        e.preventDefault()
        setError(null)
        setLoading(true)
        try {
            await login(email, password)
            navigate(resolvePostLoginTarget(), { replace: true })
        } catch (err) {
            setError(getLoginErrorMessage(err))
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex flex-col">
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-sm">
                    <div className="flex flex-col items-center gap-3 mb-6">
                        <div className="w-64 h-20 overflow-hidden">
                            <img
                                src={LOGIN_LOGO_URL}
                                alt="설계 EZ"
                                className="w-full h-full object-cover object-center"
                            />
                        </div>
                        <p className="text-base text-slate-600 text-center leading-snug">
                            보험 설계의 표준을 재정의하다.<br />
                            더 안전하게, 더 빠르게.
                        </p>
                    </div>
                    <form onSubmit={onSubmit} className="w-full space-y-4 p-6 border rounded-lg bg-white">
                    <h1 className="text-xl font-semibold">로그인</h1>
                    {error && <div className="text-red-600 text-sm whitespace-pre-line">{error}</div>}
                    <div>
                        <label className="block text-sm text-slate-600">이메일</label>
                        <input
                            className="mt-1 w-full rounded border px-3 py-2"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={(!email && emailHint) ? `예: ${emailHint}` : ''}
                            required
                        />
                        {emailHint && !email && (
                            <div className="text-xs text-slate-500 mt-1">
                                아이디 찾기 결과: <span className="font-mono">{emailHint}</span>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm text-slate-600">비밀번호</label>
                        <input className="mt-1 w-full rounded border px-3 py-2" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    </div>
                    <button className="w-full rounded bg-brand-navy text-white py-2 disabled:opacity-50" type="submit" disabled={loading}>
                        {loading ? <Loading label="로그인 중…" /> : '로그인'}
                    </button>

                    {socialConfigured && (
                        <div className="pt-2 border-t">
                            <div className="text-xs text-slate-500 mb-2">소셜 로그인</div>
                            <div className="space-y-2">
                                {googleEnabled && (
                                    <div className="w-full">
                                        <GoogleLogin
                                            useOneTap={false}
                                            onSuccess={async (cred) => {
                                                try {
                                                    setError(null)
                                                    setLoading(true)
                                                    const idToken = cred?.credential
                                                    if (!idToken) throw new Error('구글 토큰을 받지 못했습니다.')
                                                    await loginWithGoogle(idToken)
                                                    navigate(resolvePostLoginTarget(), { replace: true })
                                                } catch (e) {
                                                    setError(getLoginErrorMessage(e))
                                                } finally {
                                                    setLoading(false)
                                                }
                                            }}
                                            onError={() => setError('구글 로그인에 실패했습니다.')}
                                            width="100%"
                                        />
                                    </div>
                                )}
                                {kakaoEnabled && (
                                    <button
                                        type="button"
                                        className="w-full rounded bg-[#FEE500] text-[#191919] py-2 text-sm font-semibold disabled:opacity-50"
                                        disabled={loading}
                                        onClick={async () => {
                                            try {
                                                setError(null)
                                                setLoading(true)
                                                const token = await kakaoLogin({ scope: 'account_email' })
                                                await loginWithKakao(token)
                                                navigate(resolvePostLoginTarget(), { replace: true })
                                            } catch (e) {
                                                setError(getLoginErrorMessage(e))
                                            } finally {
                                                setLoading(false)
                                            }
                                        }}
                                    >
                                        카카오로 로그인
                                    </button>
                                )}
                                {naverEnabled && (
                                    <button
                                        type="button"
                                        className="w-full rounded bg-[#03C75A] text-white py-2 text-sm font-semibold disabled:opacity-50"
                                        disabled={loading}
                                        onClick={async () => {
                                            try {
                                                setError(null)
                                                setLoading(true)
                                                const { code, state, redirectUri } = await naverLogin({})
                                                await loginWithNaverCode({ code, state, redirectUri })
                                                navigate(resolvePostLoginTarget(), { replace: true })
                                            } catch (e) {
                                                setError(getLoginErrorMessage(e))
                                            } finally {
                                                setLoading(false)
                                            }
                                        }}
                                    >
                                        네이버로 로그인
                                    </button>
                                )}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-2">
                                소셜 로그인은 사전에 연동된 계정만 가능합니다. (가입 후 연동 → 승인 후 사용)
                            </div>
                        </div>
                    )}
                    <div className="flex items-center justify-between text-sm text-slate-600">
                        <Link to="/find-id" className="text-brand-navy font-semibold underline">아이디 찾기</Link>
                        <Link to="/forgot-password" className="text-brand-navy font-semibold underline">비밀번호 찾기</Link>
                    </div>
                    <div className="text-sm text-slate-600">
                        <span>가입을 원하시면 </span>
                        <Link to="/signup" className="text-brand-navy font-semibold underline">
                            회원가입 신청
                        </Link>
                        <span>을 이용해 주세요.</span>
                    </div>
                    </form>
                </div>
            </main>
            <PublicFooter />
        </div>
    )
}
